import { Center, VStack } from '@chakra-ui/react';
import { useSearchParams } from 'next/navigation';

import { motion, MotionProps } from 'framer-motion';
import { useMemo } from 'react';
import { NetworksGrid, OutletsGrid, Pagination } from 'components';
import { resolvePath } from 'utils/helpers';
import { Paths } from 'utils/paths';

import { useResponsiveness } from 'hooks/useResponsiveness';
import { usePageTextContent } from 'context/pageTextContent';
import { useSearchPageStore } from '../../useSearchPageStore';
import { SearchResult } from './SearchResult';

interface Props {
  networks: AdaptedNetwork[];
  stores: Paginated<AdaptedStore>;
}

export const Search = (props: Props) => {
  const { networks, stores } = props;

  const { content } = usePageTextContent<SearchPageContent>();

  const { networksGiftcard, storesGiftcard } = content;

  const isMapOpen = useSearchPageStore(state => state.isMapOpen);
  const isLoading = useSearchPageStore(state => state.isLoading);

  const { isMobile } = useResponsiveness();

  const searchParams = useSearchParams();

  const currentPage = searchParams?.get('page');
  const isFirstPage = !currentPage || currentPage === '1';

  const getPageHref = (page: number) => {
    const pageHref = resolvePath({ pathname: Paths.SEARCH, query: { page } });

    const newSearchParams = new URLSearchParams(searchParams?.toString());

    newSearchParams.delete('page');

    const stringParams = newSearchParams.toString();

    if (!stringParams) return pageHref;

    return `${pageHref}&${stringParams}`;
  };

  const hasNetworks = networks.length > 0;

  const containerAnimation = useMemo<MotionProps['animate']>(() => {
    if (isMobile) return { maxWidth: '100%' };

    return { maxWidth: isMapOpen ? '43.75rem' : '90rem' };
  }, [isMapOpen, isMobile]);

  return (
    <VStack
      as={motion.div}
      animate={containerAnimation}
      spacing="2.125rem"
      align={['center', 'center', 'flex-start']}
      w="full"
      px={['1.25rem', '1.25rem', '2.5rem']}
      py="2.5rem"
      borderTopRadius={['lg', 'lg', 0]}
    >
      {hasNetworks && isFirstPage && (
        <SearchResult title={networksGiftcard}>
          <NetworksGrid
            networks={networks}
            isLoading={isLoading}
            gap={['0.5rem', '0.5rem', isMapOpen ? '1.5rem' : '1rem']}
            gridTemplateColumns={[
              'repeat(2, minmax(10rem, 1fr))',
              'repeat(2, minmax(10rem, 1fr))',
              `repeat(${isMapOpen ? 2 : 4}, minmax(20%, 1fr))`,
            ]}
          />
        </SearchResult>
      )}

      <SearchResult title={storesGiftcard}>
        <OutletsGrid
          outlets={stores.items}
          isCardHorizontal={isMapOpen}
          isLoading={isLoading}
        />
      </SearchResult>

      <Center w="full">
        <Pagination pagination={stores} getPageHref={getPageHref} />
      </Center>
    </VStack>
  );
};
